/**
 * Públicos de la base (`/activa/oficial/base/publicos`), SPEC-BD §6.4.
 *
 * El conteo de la tabla es el último que se calculó (con «calculado hace X»): recalcularlos todos al
 * entrar sería una cuenta sobre toda la base por cada público. Al abrir uno se recalcula, y «Recalcular»
 * lo hace desde la fila (el mismo `GET /base/publicos/:id`, que además lo deja guardado). Hacía falta: la
 * fatiga, las bajas y los límites de Meta cambian con las horas, y en la recorrida de QA un público decía
 * «48 pueden recibir» de hacía 4 h mientras el chequeo de la campaña decía 0 (en el medio salió una
 * campaña a esos 48). Sin abrir el público no había forma de traer el número de ahora.
 * Borrar un público no borra contactos; si lo usa una campaña en borrador, el backend lo frena con
 * `PUBLICO_EN_USO` y la lista de esas campañas, que se muestran con su enlace.
 */

import { useCallback, useEffect, useId, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Copy, Loader2, Pencil, Plus, Send, Trash2, UsersRound, X } from "lucide-react";
import type { PublicoDetalle, PublicoResumen } from "../../types.base";
import { baseApi } from "../../baseApi";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import { fecha, num } from "../../../campana-nueva/utils/format";
import { CajaError, Esqueleto, R_BASE, haceCuanto, llamar, textoFallo } from "../../components/base/comun";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

const R_CAMPANAS = "/activa/oficial/campanas";

type EnUso = { nombre: string; campanas: { id: string; nombre: string }[] };

export default function PublicosPage() {
  const location = useLocation();
  const equipo = useEsEquipoDeenex();
  const [items, setItems] = useState<PublicoResumen[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState((location.state as { aviso?: string } | null)?.aviso || "");
  const [errorAccion, setErrorAccion] = useState("");
  const [enUso, setEnUso] = useState<EnUso | null>(null);
  const [ocupado, setOcupado] = useState("");
  const [renombrando, setRenombrando] = useState<PublicoResumen | null>(null);
  const [recalculando, setRecalculando] = useState("");

  const cargar = useCallback(async () => {
    const l = await llamar<PublicoResumen[]>(() => baseApi.publicos());
    setCargando(false);
    if (!l.ok) { setError(textoFallo(l)); return; }
    setItems(Array.isArray(l.data) ? l.data : []);
    setError("");
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  const limpiarAvisos = () => { setAviso(""); setErrorAccion(""); setEnUso(null); };

  const duplicar = async (p: PublicoResumen) => {
    limpiarAvisos();
    setOcupado(p.id);
    const l = await llamar<PublicoDetalle>(() => baseApi.duplicarPublico(p.id));
    setOcupado("");
    if (!l.ok || !l.data) { setErrorAccion(l.ok ? "El servidor no devolvió la copia." : textoFallo(l)); return; }
    setAviso(`Se creó «${l.data.nombre}».`);
    void cargar();
  };

  /** Vuelve a contar un público y deja la cifra nueva en su fila (el backend también la guarda). */
  const recalcular = async (p: PublicoResumen) => {
    limpiarAvisos();
    setRecalculando(p.id);
    const l = await llamar<PublicoDetalle>(() => baseApi.publico(p.id));
    setRecalculando("");
    const c = l.ok ? l.data?.conteoActual : null;
    if (!l.ok || !c) { setErrorAccion(l.ok ? "El servidor no devolvió el conteo." : textoFallo(l)); return; }
    const conteo = { total: c.total, puedenRecibir: c.puedenRecibir, at: c.calculadoAt };
    setItems((xs) => (xs ? xs.map((x) => (x.id === p.id ? { ...x, conteo } : x)) : xs));
  };

  const borrar = async (p: PublicoResumen) => {
    limpiarAvisos();
    if (!window.confirm(`¿Borrar el público "${p.nombre}"? Los contactos no se borran.`)) return;
    setOcupado(p.id);
    const l = await llamar<{ borrado: true }>(() => baseApi.borrarPublico(p.id));
    setOcupado("");
    if (!l.ok) {
      if (l.code === "PUBLICO_EN_USO") {
        const campanas = Array.isArray(l.extra?.campanas)
          ? (l.extra.campanas as unknown[]).filter((c): c is { id: string; nombre: string } =>
            !!c && typeof c === "object" && typeof (c as { id?: unknown }).id === "string" && typeof (c as { nombre?: unknown }).nombre === "string")
          : [];
        setEnUso({ nombre: p.nombre, campanas });
        return;
      }
      setErrorAccion(textoFallo(l));
      return;
    }
    setAviso(`Se borró «${p.nombre}».`);
    void cargar();
  };

  // Armar, renombrar, duplicar y borrar públicos es del equipo de Deenex: un cliente los ve.
  const boton = equipo ? (
    <Link to={`${R_BASE}/publicos/nuevo`} className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2">
      <Plus size={16} /> Nuevo público
    </Link>
  ) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-slate-900">Públicos</h3>
        {boton}
      </div>

      {aviso && <p className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" role="status"><CheckCircle2 size={15} /> {aviso}</p>}
      {errorAccion && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{errorAccion}</p>}
      {enUso && (
        <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {enUso.campanas.length ? (
            <p>
              Lo usan campañas en borrador:{" "}
              {enUso.campanas.map((c, i) => (
                <span key={c.id}>
                  {i > 0 && ", "}
                  <Link to={`${R_CAMPANAS}/${encodeURIComponent(c.id)}/editar`} className="font-semibold underline">{c.nombre}</Link>
                </span>
              ))}
              . Cambiales la audiencia primero.
            </p>
          ) : <p>Lo usan campañas en borrador: cambiales la audiencia primero.</p>}
        </div>
      )}

      {cargando && !items ? (
        <Esqueleto filas={4} alto="h-12" />
      ) : error && !items ? (
        <CajaError titulo="No pudimos traer los públicos." detalle={error} onReintentar={() => { setCargando(true); void cargar(); }} />
      ) : !items?.length ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <UsersRound size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
            {"Todavía no armaste públicos. Un público es un grupo de contactos de tu base, por ejemplo: \"los de la Feria 2026 que tienen email\"."}
          </p>
          <div className="mt-4">{boton}</div>
        </div>
      ) : (
        <div className="relative overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
          {/* `relative`: sin él, el «Acciones» sólo para lectores de pantalla (absolute) se escapaba del scroll
              de la tabla y en el celular hacía que la página entera se corriera de costado. */}
          {error && <p className="px-4 pt-3 text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5">Tipo</th>
                <th className="px-4 py-2.5">Condiciones</th>
                <th className="px-4 py-2.5 text-right">Contactos</th>
                <th className="px-4 py-2.5 text-right">Pueden recibir</th>
                <th className="px-4 py-2.5">Actualizado</th>
                <th className="px-4 py-2.5"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => {
                const ruta = `${R_BASE}/publicos/${encodeURIComponent(p.id)}`;
                const ocupadoEste = ocupado === p.id;
                return (
                  <tr key={p.id} className="align-top">
                    <td className="px-4 py-3">
                      {equipo ? <Link to={ruta} className="font-semibold text-slate-900 hover:underline">{p.nombre}</Link> : <span className="font-semibold text-slate-900">{p.nombre}</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {p.tipo === "estatico" ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Fijo</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">Dinámico</span>
                      )}
                      {p.tipo === "estatico" && p.congeladoAt && <p className="text-[11px] text-slate-400 mt-0.5">del {fecha(p.congeladoAt)}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 max-w-[18rem]">
                      {p.condiciones.length ? (
                        <>
                          {p.condiciones.slice(0, 2).join(" · ")}
                          {p.condiciones.length > 2 && <span className="text-slate-400"> +{p.condiciones.length - 2}</span>}
                        </>
                      ) : <span className="text-slate-400">Toda la base</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.conteo ? num(p.conteo.total) : "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {recalculando === p.id ? <Loader2 size={13} className="inline animate-spin text-slate-400" aria-label="Contando" /> : p.conteo ? num(p.conteo.puedenRecibir) : "—"}
                      {p.conteo && recalculando !== p.id && <p className="text-[11px] text-slate-400 whitespace-nowrap">calculado {haceCuanto(p.conteo.at)}</p>}
                      <div>
                        <button type="button" onClick={() => void recalcular(p)} disabled={!!recalculando}
                          aria-label={`Recalcular «${p.nombre}»`}
                          className="text-[11px] font-semibold text-violet-700 hover:underline disabled:opacity-50 whitespace-nowrap">
                          Recalcular
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fecha(p.updatedAt)}</td>
                    <td className="px-4 py-3">
                      {equipo && <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Link to={`${R_CAMPANAS}/nueva?publico=${encodeURIComponent(p.id)}`} title="Usar en una campaña"
                          className="h-8 px-2.5 rounded-lg bg-violet-50 text-violet-700 text-xs font-semibold hover:bg-violet-100 inline-flex items-center gap-1 whitespace-nowrap">
                          <Send size={12} /> Usar en una campaña
                        </Link>
                        <BotonIcono label="Duplicar" onClick={() => void duplicar(p)} disabled={!!ocupado}>
                          {ocupadoEste ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />}
                        </BotonIcono>
                        <BotonIcono label="Renombrar" onClick={() => { limpiarAvisos(); setRenombrando(p); }} disabled={!!ocupado}><Pencil size={13} /></BotonIcono>
                        <BotonIcono label="Borrar" onClick={() => void borrar(p)} disabled={!!ocupado} peligro><Trash2 size={13} /></BotonIcono>
                      </div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {renombrando && (
        <ModalRenombrar
          publico={renombrando}
          onListo={(nombre) => { setRenombrando(null); setAviso(`Ahora se llama «${nombre}».`); void cargar(); }}
          onCancelar={() => setRenombrando(null)}
        />
      )}
    </div>
  );
}

function BotonIcono({ label, onClick, disabled, peligro = false, children }: {
  label: string; onClick: () => void; disabled: boolean; peligro?: boolean; children: ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={`h-8 px-2.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50 ${
        peligro ? "border-red-100 text-red-700 hover:bg-red-50" : "border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}>
      {children}<span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function ModalRenombrar({ publico, onListo, onCancelar }: { publico: PublicoResumen; onListo: (nombre: string) => void; onCancelar: () => void }) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [nombre, setNombre] = useState(publico.nombre);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const idTitulo = useId();
  const idNombre = useId();
  const limpio = nombre.trim();
  const valido = limpio.length >= 1 && limpio.length <= 80 && limpio !== publico.nombre;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !guardando) onCancelar(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [guardando, onCancelar]);

  const guardar = async () => {
    if (!valido || guardando) return;
    setGuardando(true);
    setError("");
    const l = await llamar<PublicoDetalle>(() => baseApi.editarPublico(publico.id, { nombre: limpio }));
    setGuardando(false);
    if (!l.ok || !l.data) {
      setError(!l.ok && l.code === "NOMBRE_PUBLICO_EN_USO" ? "Ya hay un público con ese nombre." : l.ok ? "El servidor no devolvió el público." : textoFallo(l));
      return;
    }
    onListo(l.data.nombre);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby={idTitulo} className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id={idTitulo} className="font-semibold text-lg text-slate-900">Renombrar</h3>
          <button onClick={onCancelar} disabled={guardando} aria-label="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>
        <form className="p-6 space-y-2" onSubmit={(e) => { e.preventDefault(); void guardar(); }}>
          <label htmlFor={idNombre} className="text-xs font-semibold text-slate-600">Nombre</label>
          <input id={idNombre} value={nombre} maxLength={80} disabled={guardando} onChange={(e) => setNombre(e.target.value.slice(0, 80))}
            className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onCancelar} disabled={guardando}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Volver
            </button>
            <button type="submit" disabled={!valido || guardando}
              className="flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
